/**
 * Entry point for the application
 *
 * features:
 * - Query client provider for react-query
 * - Styled snackbar provider for notifications
 * - Global styling through global.css
 * - App component as root
 */
import { StrictMode } from 'react';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { TooltipProvider } from '@monksflow/monks-ui';
import { createRoot } from 'react-dom/client';
import { OpenFeatureProvider } from '@openfeature/react-sdk';
import { I18nextProvider } from 'react-i18next';
import App from '@/App.tsx';
import { StyledSnackbarProvider } from '@/app/providers/NotificationProvider';
import * as uiStoreModule from '@/app/store/ui';
import * as widgetStoreModule from '@/app/store/widget';
import * as widgetHooksModule from '@/app/store/widget/widget-hooks';
import { configureWidgetStores, type WidgetStoreBindings } from '@/entities/widget';
import { getWorkspaceId } from '@/entities/workspace';
import * as chatsModule from '@/features/chats';
import {
  configureChatStores,
  sendOrCreateChat,
  useIsAiBusy,
  type ChatStoreBindings,
} from '@/features/chats';
import { launchAgentWithWidget } from '@/entities/agent';
import { UploadToast } from '@/features/upload-queue';
import { useChatAgentMcp } from '@/features/user-mcp-servers';
import { InlineHtmlWidgets } from '@/features/html-widget';
import { ELEMENT_PICKER_ACTIVATION_KEY } from '@/shared/config/element-picker.constants';
import { AdaptationProjectPickerModal } from '@/features/adaptation-project-picker';
import { configureChatActions } from '@/lib/chat-actions/chat-actions-bindings';
import { configureChatMcp } from '@/lib/chat-mcp';
import { configureChatInlineWidgets } from '@/lib/chat-inline-widgets';
import { enqueueUploadImpl } from '@/lib/upload-queue/enqueue-upload-impl';
import { configureUploadQueueBindings } from '@/lib/upload-queue/upload-queue-bindings';
import { configureWorkspaceContext } from '@/lib/workspace-context/workspace-context-bindings';
import { queryClient } from '@/lib/config';
// Side-effect import: `agents.config.ts` calls `configureAgentMetadata` at
// module load to bind the `entities/agent` agent-metadata helpers seam with
// its concrete agent map.
//
// We import explicitly here (instead of relying on transitive imports via
// `AgentAvatar` / `SidePanel`) so the module is
// guaranteed PRESENT in the import graph and therefore evaluated at startup
// — preventing accidental tree-shaking and making the wiring intent visible
// in main.tsx alongside `configureChatStores` / `configureWidgetStores`.
//
// NOTE on ordering: ESM loads depth-first. The transitive imports on lines
// above (`@/entities/widget`, `@/features/chats`) are fully evaluated before
// this `import` statement runs — that's a property of the ESM spec, not of
// this line. The actual safety property we rely on is "no module-level call
// to `getAgentMetadataHelpers()` exists in any consumer's transitive graph"
// (helpers are invoked from inside hooks/event handlers, not at module load).
// If a future contributor adds a top-level `getAgentMetadataHelpers()` call
// somewhere upstream, this side-effect import would NOT save them.
import '@/lib/config/agents.config';
import { configureStreamSaver } from '@/lib/streamsaver';
import '@/styles/global.css';
import { initAnalytics, AnalyticsProvider } from '@/shared/analytics';
import { i18n } from '@/shared/i18n';
import { initializeApp } from './initializeApp';

// ---- Chats slice store bindings ----
// `features/chats/` MUST NOT value-import from `@/app/store/...` (FSD layer
// boundary). Internals call `getChatStores()` to obtain UI + widget stores
// via this binding. Wire ONCE here, before any chats code runs (any
// `<ChatSessionLayout>`, any `useChatStore` hook, any `setAgentById`
// action).
//
// Annotate the binding variable
// with its full binding type (`const x: ChatStoreBindings['uiStore'] =
// { ... }`) and cast each member individually via `as unknown as
// ChatStoreBindings['uiStore'][<key>]`. The variable-level annotation
// guarantees that EVERY required member of the binding interface MUST
// be provided — adding a new required member to the binding errors
// HERE (the literal is missing it). The per-member `as unknown as`
// only bridges the loose index-signature shape (`[key: string]:
// unknown`) on the binding's state types versus the concrete zustand
// store's exact union types — it does NOT hide missing required
// members the way the previous file-level `as unknown as
// ChatStoreBindings['uiStore']` did.
//
// Why not `satisfies`: the binding's `ZustandLikeStoreHook` declares a
// bivariant call signature plus index-signature state shape that
// neither matches the concrete `UseBoundStore<UIStore>` exact union
// (the index signature is "wider", not narrower). Both `satisfies` and
// per-member explicit-type casts (`as ChatStoreBindings['uiStore']
// ['useUIStore']`) fail with TS2352 because the conversion is too
// distant. The unknown bridge is the ONLY mechanically valid path
// here — but kept narrow to per-member so it remains belt-and-
// suspenders with the variable annotation enforcing required members.
const chatsUiBinding: ChatStoreBindings['uiStore'] = {
  useUIStore: uiStoreModule.useUIStore as unknown as ChatStoreBindings['uiStore']['useUIStore'],
  useAgentModal: uiStoreModule.useAgentModal,
  useSidePanelType: uiStoreModule.useSidePanelType,
  useSidePanelLayoutMode:
    uiStoreModule.useSidePanelLayoutMode as unknown as ChatStoreBindings['uiStore']['useSidePanelLayoutMode'],
};

const chatsWidgetBinding: ChatStoreBindings['widgetStore'] = {
  useWidgetStore:
    widgetStoreModule.useWidgetStore as unknown as ChatStoreBindings['widgetStore']['useWidgetStore'],
};

configureChatStores({
  uiStore: chatsUiBinding,
  widgetStore: chatsWidgetBinding,
});

// ---- Workspace-context binding ----
// `lib/api/interceptors/workspace-id.interceptor.ts` injects the active
// workspace id into outbound HTTP requests. Lib must NOT depend on
// entities (FSD layer order), so the interceptor reads through this
// binding seam — wired here at boot from `entities/workspace`.
configureWorkspaceContext({
  getWorkspaceId,
});

// ---- Chat-actions binding ----
// `features/audiences` (and any other feature that triggers chat
// orchestration without being chats itself) reads chat actions through
// this seam to avoid `feature → feature` value imports. Wired here at
// boot from `@/features/chats`.
configureChatActions({
  launchAgentWithWidget,
  sendOrCreateChat,
  useIsChatBusy: useIsAiBusy,
});

// ---- Chat-MCP binding ----
// The chat composer (`features/chats`) surfaces an agent's MCP servers + connection status in its
// settings dropdown. To avoid a `features/chats → features/user-mcp-servers` value edge (FSD layer
// order, enforced by `features/chats/fsd-boundaries.test.ts`), the composer reads this capability
// through the `@/lib/chat-mcp` seam — wired here at boot from `@/features/user-mcp-servers`.
configureChatMcp({ useChatAgentMcp });

// ---- Chat inline-widgets binding ----
// Agent replies mount their inline widgets through `@/lib/chat-inline-widgets`, so `features/chats`
// stays free of `@/features/html-widget`.
configureChatInlineWidgets({ InlineWidgets: InlineHtmlWidgets });

// ---- Upload-queue enqueue binding ----
// The 3 upload wires (create-project wizard, upload-project-file folder,
// manage-project-artifacts library) reach the global queue via this seam
// to avoid `features/X → features/upload-queue` value edges (FSD layer
// order). Wired here at boot with the vanilla enqueue implementation from
// the queue feature.
configureUploadQueueBindings({ enqueueUpload: enqueueUploadImpl });

// ---- Widget leaf-feature store bindings ----
// `features/<x>-widget/` MUST NOT value-import from `@/app/store/...` or
// sibling features (FSD layer boundary). Internals call `getWidgetStores()`
// to obtain chat + widget + UI stores via this binding. Wire ONCE here,
// before any widget code runs (any side panel render, any widget version
// hook call).
//
// Same pattern as
// `chatsUiBinding`: variable-level annotation
// (`WidgetStoreBindings['chatStore']`) guarantees every required member
// MUST appear here (TS errors at the literal if a new required member is
// added to the binding interface). Per-member `as unknown as` only
// bridges the index-signature mismatch on members whose source types
// don't carry `[key: string]: unknown` (the chats hooks return strict
// state unions, while the binding state mirrors are loosely typed). No
// more file-level `as unknown as WidgetStoreBindings[...]` widening.
//
// Note: `widgetHooksModule` (`useWidgetVersions`, `useCreateArtifactVersion`)
// is wired into the `widgetStore` binding so widget leaf features get the
// same pre-bound hooks they use today, without importing from
// `@/app/store/widget/widget-hooks` directly.
const widgetChatBinding: WidgetStoreBindings['chatStore'] = {
  useChatStore:
    chatsModule.useChatStore as unknown as WidgetStoreBindings['chatStore']['useChatStore'],
  useChatActions: chatsModule.useChatActions,
  getChatInfo: chatsModule.getChatInfo,
  getFileDownloadUrl: chatsModule.getFileDownloadUrl,
  useChatId: chatsModule.useChatId,
  useMessages:
    chatsModule.useMessages as unknown as WidgetStoreBindings['chatStore']['useMessages'],
  useUpdateAgentState:
    chatsModule.useUpdateAgentState as unknown as WidgetStoreBindings['chatStore']['useUpdateAgentState'],
  useChatStoreInput:
    chatsModule.useChatStoreInput as unknown as WidgetStoreBindings['chatStore']['useChatStoreInput'],
  useFileAttachments:
    chatsModule.useFileAttachments as unknown as WidgetStoreBindings['chatStore']['useFileAttachments'],
  ChatMarkdownRenderer:
    chatsModule.ChatMarkdownRenderer as unknown as WidgetStoreBindings['chatStore']['ChatMarkdownRenderer'],
  SUPPORTED_IMAGE_MODELS: chatsModule.SUPPORTED_IMAGE_MODELS,
};

const widgetWidgetBinding: WidgetStoreBindings['widgetStore'] = {
  useWidgetStore:
    widgetStoreModule.useWidgetStore as unknown as WidgetStoreBindings['widgetStore']['useWidgetStore'],
  useWidgetPanel:
    widgetStoreModule.useWidgetPanel as unknown as WidgetStoreBindings['widgetStore']['useWidgetPanel'],
  useWidget:
    widgetStoreModule.useWidget as unknown as WidgetStoreBindings['widgetStore']['useWidget'],
  useWidgetError: widgetStoreModule.useWidgetError,
  useWidgetActions: widgetStoreModule.useWidgetActions,
  useWidgetVersions: widgetHooksModule.useWidgetVersions,
  useCreateArtifactVersion: widgetHooksModule.useCreateArtifactVersion,
};

const widgetUiBinding: WidgetStoreBindings['uiStore'] = {
  useUIStore: uiStoreModule.useUIStore as unknown as WidgetStoreBindings['uiStore']['useUIStore'],
  useSidePanel: uiStoreModule.useSidePanel,
  useSidePanelLayoutMode:
    uiStoreModule.useSidePanelLayoutMode as unknown as WidgetStoreBindings['uiStore']['useSidePanelLayoutMode'],
};

configureWidgetStores({
  chatStore: widgetChatBinding,
  widgetStore: widgetWidgetBinding,
  uiStore: widgetUiBinding,
});

// ---- Amplitude Analytics + Session Replay ----
initAnalytics();

// ---- Sentry init ----
if (import.meta.env.SENTRY_ENABLED === 'true') {
  Sentry.init({
    dsn: import.meta.env.SENTRY_DSN as string,
    environment: import.meta.env.SENTRY_ENVIRONMENT as string,
    release: import.meta.env.SENTRY_RELEASE as string,
    sendDefaultPii: import.meta.env.SENTRY_SEND_DEFAULT_PII === 'true',
    enableLogs: import.meta.env.SENTRY_ENABLE_LOGS === 'true',
    integrations: [
      Sentry.replayIntegration({
        // Default replay compression spawns a Web Worker from a blob: URL,
        // which our CSP blocks (no worker-src; script-src has no blob:).
        // Disabling sends ~3x larger uncompressed payloads to Sentry but
        // unblocks replay until CSP is relaxed at the infra layer.
        useCompression: false,
        // NOTE: This will disable built-in masking.
        // Only use this if your site has no sensitive data, or if you've already set up other options for masking or blocking relevant data, such as 'ignore', 'block', 'mask' and 'maskFn'.
        maskAllText: false,
        blockAllMedia: false,
        block: ['.blocked-css-element'],
      }),
    ],
    // Session Replay sampling
    replaysSessionSampleRate: Number(import.meta.env.SENTRY_REPLAY_SAMPLE_RATE),
    replaysOnErrorSampleRate: Number(import.meta.env.SENTRY_REPLAY_ERROR_SAMPLE_RATE),
  });
}

if (import.meta.env.DEV) {
  // React Grab's own gesture is Cmd+C, which the Content Studio canvas already uses to copy the
  // selected formats - pressing it over a board opened the inspector on top of the copy. Alt+C
  // collides with nothing (the canvas ignores Alt-modified letters outright), and the input guard
  // keeps the tool out of the way while someone is typing, where Alt+C composes a character.
  // The module inits itself on import, so this reconfigures that instance rather than making one.
  void import('react-grab').then(({ getGlobalApi }) => {
    getGlobalApi()?.setOptions({
      activationKey: ELEMENT_PICKER_ACTIVATION_KEY,
      allowActivationInsideInput: false,
    });
  });
}

// ---- StreamSaver self-hosted bridge ----
// Points streamSaver.mitm at /streamsaver/mitm.html (vendored under public/)
// instead of the default jimmywarting.github.io URL. Must run before any
// download flow invokes streamSaver.createWriteStream().
configureStreamSaver();

// Wait for app initialization (auth, workspace, stores) before rendering
void initializeApp().then(() => {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <I18nextProvider i18n={i18n}>
          <OpenFeatureProvider>
            <StyledSnackbarProvider>
              {/* Radix throws when a `Tooltip` finds no provider above it, so this has to
                  be here rather than around the one surface that wants a tooltip: an
                  icon-only control is shared code, and the host it renders in decides
                  nothing about whether its label can be shown. Surfaces that need a
                  different delay still mount their own provider over this one. */}
              <TooltipProvider>
                <AnalyticsProvider>
                  <App />
                  {/* Global upload-queue surface.
                      Sibling of <App /> so it persists across route
                      changes — it observes the global zustand store
                      directly and is anchored `fixed bottom-6 right-6
                      z-50` so it never participates in router layout.
                      NOTE: the notistack provider also anchors bottom-right,
                      so the two surfaces share that corner — see
                      NotificationProvider. */}
                  <UploadToast />
                  {/* Global Adaptation project-picker modal — sibling of <App/>
                      so it persists across route changes and observes the shared
                      adaptation-picker store directly (same pattern as <UploadToast/>). */}
                  <AdaptationProjectPickerModal />
                </AnalyticsProvider>
              </TooltipProvider>
            </StyledSnackbarProvider>
          </OpenFeatureProvider>
        </I18nextProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
});
